import type { ReactNode } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { signOut } from "@/lib/auth";
import { ForcePasswordChangeGate } from "@/components/auth/ForcePasswordChangeGate";
import { isSupportStagingRoute, supportStagingHome, type SupportStagingState } from "@/lib/supportStaging";

export function SupportStagingEnvironmentBoundary({ state, children }: { state: SupportStagingState; children: ReactNode }) {
  if (state === "blocked") return <main className="mx-auto max-w-xl p-8" role="alert"><h1 className="text-xl font-bold">Support staging unavailable</h1><p>The staging project, mode and hosted address must match before signing in.</p></main>;
  return <>{state === "staging" && <div role="status" className="sticky top-0 z-50 bg-amber-200 px-4 py-2 text-center font-semibold text-amber-950">Isolated support staging · Test data only</div>}{children}</>;
}

export function SupportStagingRouteBoundary({ active, children }: { active: boolean; children: ReactNode }) {
  const { pathname } = useLocation();
  const { realRole, loading } = useAuth();
  if (!active || isSupportStagingRoute(pathname)) return <>{children}</>;
  if (loading) return <p role="status" className="p-6">Loading support...</p>;
  // Use the authenticated role, never an impersonation overlay, for staging entry routing.
  return <Navigate to={supportStagingHome(realRole)} replace />;
}

// The production shell mounts an assistant and navigation data subscriptions unrelated to support.
// Staging keeps the same protected support pages in a small, support-only staff shell.
export function SupportStagingStaffShell() {
  const { realOrgUser } = useAuth();
  return <div className="min-h-screen bg-sidebar text-text">
    <header className="border-b border-border bg-white px-6 py-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="font-heading text-xl font-bold">BiG Support</h1><p className="text-sm text-text-muted">{realOrgUser?.email}</p></div>
        <nav aria-label="Staff support navigation" className="flex flex-wrap items-center gap-4 text-sm">
          <NavLink to="/admin/support/tickets">Tickets</NavLink>
          <NavLink to="/admin/support/requesters">Requesters</NavLink>
          <NavLink to="/admin/support/agreements">Agreements</NavLink>
          <NavLink to="/admin/support/rate-cards">Rate cards</NavLink>
          <NavLink to="/admin/support/close">Monthly close</NavLink>
          <NavLink to="/admin/apps/support">Organizations</NavLink>
          <button onClick={() => void signOut()}>Sign out</button>
        </nav>
      </div>
    </header>
    <main className="mx-auto max-w-7xl p-6"><ForcePasswordChangeGate><Outlet /></ForcePasswordChangeGate></main>
  </div>;
}
